import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import Loader from '../components/Loader';
import paymentsService from '../services/paymentsService';
import styles from '../assets/markAsPaidInitial.module.css';

const MarkAsPaid = () => {
  const { rentId, paymentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [formData, setFormData] = useState({
    amount_paid: '',
    payment_date: '',
    bank_account_id: '',
    notes: ''
  });
  const [error, setError] = useState(null);

  // Refs for date picker inputs
  const paymentDateInputRef = useRef(null);

  useEffect(() => {
    const fetchMarkAsPaidData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('[MARK_AS_PAID] Fetching data for rentId:', rentId, 'paymentId:', paymentId);

        const result = await paymentsService.markPaymentAsPaidInitial(rentId, paymentId);
        console.log('[MARK_AS_PAID] Data received:', result);

        if (!result.success) {
          throw new Error(result.message || 'Failed to fetch mark as paid data');
        }

        setData(result);

        // Initialize form data with toPay amount and payment details
        const today = formatDateForDisplay(new Date());
        setFormData(prev => ({
          ...prev,
          amount_paid: result.toPay?.toString() || '',
          payment_date: today,
          bank_account_id: result.bankAccounts?.[0]?.id?.toString() || '',
          status: '1' // Default: Paid
        }));

      } catch (err) {
        console.error('[MARK_AS_PAID] Error fetching mark as paid data:', err);
        setError(err.message || 'Failed to fetch mark as paid data. Please try again.');
        toast.error(err.message || 'Failed to fetch mark as paid data');
      } finally {
        setLoading(false);
      }
    };

    if (rentId) {
      fetchMarkAsPaidData();
    } else {
      setError('Rent ID is required');
      setLoading(false);
    }
  }, [rentId, paymentId]);

  // Format date to DD/MM/YYYY
  const formatDateForDisplay = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Convert DD/MM/YYYY to YYYY-MM-DD for date input
  const convertToDateInputFormat = (dateString) => {
    if (!dateString) return '';
    if (dateString.includes('-') && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    if (dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }
    return '';
  };

  // Convert YYYY-MM-DD to DD/MM/YYYY
  const convertFromDateInputFormat = (dateString) => {
    if (!dateString) return '';
    if (dateString.includes('-')) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    return dateString;
  };

  // Handle date change from native date picker
  const handleDateChange = (e) => {
    const dateValue = e.target.value;
    if (dateValue) {
      const formattedDate = convertFromDateInputFormat(dateValue);
      setFormData(prev => ({
        ...prev,
        payment_date: formattedDate
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        payment_date: ''
      }));
    }
  };

  // Open date picker
  const openDatePicker = (e) => {
    e?.preventDefault();
    if (paymentDateInputRef?.current) {
      setTimeout(() => {
        try {
          if (paymentDateInputRef.current.showPicker) {
            paymentDateInputRef.current.showPicker();
          } else {
            paymentDateInputRef.current.click();
          }
        } catch (err) {
          paymentDateInputRef.current.click();
        }
      }, 0);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user?.permissions?.includes('payments.manage')) {
      toast.error("You don't have permission to mark payments as paid");
      return;
    }

    try {
      setSaving(true);

      console.log('[MARK_AS_PAID] Submitting form data:', formData);

      const result = await paymentsService.submitInitialMarkAsPaid(rentId, paymentId, formData);
      console.log('[MARK_AS_PAID] Submit response:', result);

      if (result.success) {
        toast.success('Payment marked as paid successfully!');
        const redirectGuardianId = result.guardianId || data?.guardian?.id;
        if (redirectGuardianId) {
          navigate(`/guardians/${redirectGuardianId}/payments`);
        } else {
          navigate(-1);
        }
      } else {
        throw new Error(result.message || 'Failed to mark payment as paid');
      }

    } catch (err) {
      console.error('[MARK_AS_PAID] Error marking payment as paid:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to mark payment as paid';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorAlert}>
          <h4>Error</h4>
          <p>{error}</p>
          <button
            className={styles.btnMarkAsPaid}
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
          <button
            className={styles.btnCancel}
            onClick={() => navigate(-1)}
            style={{ marginLeft: '10px' }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.container}>
        <div className={styles.errorAlert}>
          <h4>Data Not Found</h4>
          <p>Unable to load payment data. Please try again.</p>
          <button
            className={styles.btnMarkAsPaid}
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
          <button
            className={styles.btnCancel}
            onClick={() => navigate(-1)}
            style={{ marginLeft: '10px' }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumb */}
      <nav className={styles.breadcrumb}>
        <ol>
          <li><Link to="/guardians">Guardians</Link></li>
          <li><Link to={`/guardians/${data.guardian?.id}`}>{data.guardian?.full_name}</Link></li>
          <li><Link to={`/guardians/${data.guardian?.id}/payments`}>Payments</Link></li>
          <li className="active">Mark as Paid</li>
        </ol>
      </nav>

      {/* Main Title */}
      <div className={styles.panelTitle}>
        <h3>Mark Initial Payment as Paid</h3>
      </div>

      {/* Payment Details Section */}
      <div className={styles.panelTitle} style={{ marginTop: '20px' }}>
        <h4>Payment Details</h4>
      </div>
      <div className={styles.panelGroup}>
        <dl className={styles.detailsList}>
          <dt>Guardian:</dt>
          <dd>
            {data.guardian?.full_name}
            {data.guardian?.unique_guardian_number && (
              <span> ({data.guardian.unique_guardian_number})</span>
            )}
          </dd>

          <dt>Status:</dt>
          <dd>
            <span
              className={styles.statusBadge}
              style={{ backgroundColor: data.guardian?.status?.color || '#f0ad4e' }}
            >
              {data.guardian?.status?.name || 'Payment Pending'}
            </span>
          </dd>

          <dt>Room:</dt>
          <dd>{data.room?.name || '-'}</dd>

          <dt>Property:</dt>
          <dd>
            {data.property?.id ? (
              <Link to={`/properties/${data.property.id}`}>{data.property.name}</Link>
            ) : (
              data.property?.name || '-'
            )}
          </dd>

          <dt>Amount to Pay:</dt>
          <dd className={styles.amountDisplay}>&pound;{data.toPay || 0}</dd>
        </dl>
      </div>

      {/* Mark as Paid Form Section */}
      <form onSubmit={handleSubmit}>
        <div className={styles.panelTitleOrange}>
          <h4>Mark as Paid</h4>
        </div>
        <div className={styles.panelGroup}>
          <div className={styles.formGroup}>
            <label>Amount Paid (&pound;) <span className={styles.required}>*</span></label>
            <input
              type="number"
              step="0.01"
              name="amount_paid"
              value={formData.amount_paid}
              onChange={handleInputChange}
              required
              className={styles.formControl}
            />
          </div>

          <div className={styles.formGroup}>
            <label>Payment Date <span className={styles.required}>*</span></label>
            <div className={styles.dateInputWrapper}>
              <input
                type="text"
                name="payment_date_display"
                value={formData.payment_date}
                readOnly
                placeholder="dd/mm/yyyy"
                className={styles.formControl}
                style={{ pointerEvents: 'none' }}
              />
              <input
                ref={paymentDateInputRef}
                type="date"
                className={styles.hiddenDateInput}
                value={convertToDateInputFormat(formData.payment_date)}
                onChange={handleDateChange}
                required
              />
              <span className={styles.calendarIcon}>
                &#128197;
              </span>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label>GG Bank Account <span className={styles.required}>*</span></label>
            <select
              name="bank_account_id"
              value={formData.bank_account_id}
              onChange={handleInputChange}
              required
              className={styles.formControl}
            >
              <option value="">Select Bank Account</option>
              {Array.isArray(data.bankAccounts) && data.bankAccounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.name || account.id}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label>Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows="3"
              className={styles.formControl}
            />
          </div>
        </div>

        {/* Property Information Section */}
        <div className={styles.panelTitle}>
          <h4>Property Information</h4>
        </div>
        <div className={styles.panelGroup}>
          <dl className={styles.propertyInfo}>
            <dt>Property Name:</dt>
            <dd>
              {data.property?.id ? (
                <Link to={`/properties/${data.property.id}`}>{data.property.name}</Link>
              ) : (
                data.property?.name || '-'
              )}
            </dd>

            <dt>Address:</dt>
            <dd>{data.property?.address_1 || data.property?.name || '-'}</dd>

            <dt>Postcode:</dt>
            <dd>{data.property?.postcode || '-'}</dd>
          </dl>
        </div>

        {/* Buttons */}
        <div className={styles.buttonGroup}>
          <button
            type="submit"
            className={styles.btnMarkAsPaid}
            disabled={saving}
          >
            {saving ? 'Marking as Paid...' : 'Mark as Paid'}
          </button>
          <button
            type="button"
            className={styles.btnCancel}
            onClick={() => navigate(-1)}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default MarkAsPaid;
